import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const ImportQueueContext = createContext({});

export const useImportQueue = () => useContext(ImportQueueContext);

export const ImportQueueProvider = ({ children }) => {
    const { token } = useAuth();

    // Array of { id, expediente, status: 'pending'|'processing'|'success'|'error', result: any, errorMsg: string }
    const [queue, setQueue] = useState(() => {
        try {
            const stored = localStorage.getItem('import_queue');
            if (stored) {
                const parsed = JSON.parse(stored);
                // Si la página se refrescó en medio de un proceso, reiniciar ese a pendiente
                return parsed.map(item => item.status === 'processing' ? { ...item, status: 'pending' } : item);
            }
        } catch (e) {
            console.error('Error loading import_queue from localStorage', e);
        }
        return [];
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [showReport, setShowReport] = useState(false); // To show the final report modal

    const processingRef = useRef(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const addExpedientes = (expedientesList, clientId = null) => {
        const newItems = expedientesList.map(exp => ({
            id: Math.random().toString(36).substr(2, 9),
            expediente: exp,
            clientId: clientId,
            status: 'pending',
        }));

        setQueue(prev => [...prev, ...newItems]);
    };

    const clearQueue = () => {
        setQueue([]);
        setShowReport(false);
    };

    const cancelImport = () => {
        setQueue(prev => prev.filter(item => item.status !== 'pending'));
        toast.info('Importación cancelada. Completando el expediente en curso...', { duration: 4000 });
    };

    const closeReport = () => {
        setShowReport(false);
        setQueue([]);
        localStorage.removeItem('import_queue');
    };

    // Auto-save queue to localStorage
    useEffect(() => {
        localStorage.setItem('import_queue', JSON.stringify(queue));
    }, [queue]);

    useEffect(() => {
        // Process queue
        const processQueue = async () => {
            if (!token) return;
            if (processingRef.current) return;

            const pendingIndex = queue.findIndex(item => item.status === 'pending');

            if (pendingIndex === -1) {
                // Queue is empty or all done
                if (queue.length > 0 && isProcessing) {
                    setIsProcessing(false);
                    setShowReport(true); // All done, show report
                    toast.success('Importación en bloque finalizada.');
                }
                return;
            }

            setIsProcessing(true);
            processingRef.current = true;

            const currentItem = queue[pendingIndex];

            // Mark as processing
            setQueue(prev => {
                const next = [...prev];
                const idx = next.findIndex(i => i.id === currentItem.id);
                if (idx !== -1) {
                    next[idx] = { ...next[idx], status: 'processing' };
                }
                return next;
            });

            try {
                // We use the bulk endpoint but send 1 by 1 to ensure UI updates progress and avoids timeouts
                const res = await axios.post(`${API}/trademarks/ingest/bulk`, {
                    expedientes: [currentItem.expediente],
                    client_id: currentItem.clientId || null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const resultData = res.data;
                // The bulk endpoint returns { total, created, already_exists, errors, results }
                const itemResult = resultData.results && resultData.results.length > 0 ? resultData.results[0] : null;

                setQueue(prev => {
                    const next = [...prev];
                    const idx = next.findIndex(i => i.id === currentItem.id);
                    if (idx !== -1) {
                        if (itemResult && itemResult.status === 'ERROR') {
                            next[idx] = { ...next[idx], status: 'error', errorMsg: itemResult.message };
                        } else {
                            next[idx] = { ...next[idx], status: 'success', result: itemResult };
                        }
                    }
                    return next;
                });

            } catch (error) {
                console.error('Error procesando expediente en cola:', currentItem.expediente, error);
                setQueue(prev => {
                    const next = [...prev];
                    const idx = next.findIndex(i => i.id === currentItem.id);
                    if (idx !== -1) {
                        next[idx] = {
                            ...next[idx],
                            status: 'error',
                            errorMsg: error.response?.data?.detail || error.message || 'Error desconocido'
                        };
                    }
                    return next;
                });
            } finally {
                processingRef.current = false;
            }
        };

        if (queue.length > 0) {
            processQueue();
        }
    }, [queue, token, API, isProcessing]);

    return (
        <ImportQueueContext.Provider value={{
            queue,
            isProcessing,
            addExpedientes,
            clearQueue,
            cancelImport,
            showReport,
            closeReport
        }}>
            {children}
        </ImportQueueContext.Provider>
    );
};
